import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, AgentModalStyles } from "./styles/adminAgents.js";
import {
  ADMIN_AGENTS_ENDPOINT,
  ADMIN_AGENTS_UPDATE_ENDPOINT,
} from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const initialForm = {
  name: "",
  address: "",
  email: "",
  phone: "",
  imageUrl: "",
  status: "active",
};

const AdminAgents = () => {
  const { user, loading: authLoading } = useAuth();

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusSelection, setStatusSelection] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    targetAgent: null,
    form: initialForm,
    saving: false,
    error: "",
  });
  const [deletingId, setDeletingId] = useState("");

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  const fetchAgents = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }
      if (statusFilter.trim()) {
        params.set("status", statusFilter.trim());
      }

      const response = await fetch(
        `${ADMIN_AGENTS_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load agents.");
      }

      const payload = await response.json();
      const list = Array.isArray(payload.agents) ? payload.agents : [];
      setAgents(list);

      if (payload.pagination) {
        const nextPage = payload.pagination.page || page;
        setPagination({
          page: nextPage,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (nextPage !== page) {
          setPage(nextPage);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: list.length,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load agents.");
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchAgents();
    }
  }, [authLoading, isAdmin, fetchAgents]);

  const tableRows = useMemo(
    () =>
      agents.map((agent) => ({
        id: agent.id || agent._id,
        name: agent.name,
        email: agent.email,
        phone: agent.phone,
        address: agent.address,
        imageUrl: agent.imageUrl,
        status: agent.status,
        raw: agent,
      })),
    [agents]
  );

  const openModal = (mode, targetAgent = null) => {
    if (mode === "edit" && targetAgent) {
      setModalState({
        show: true,
        mode: "edit",
        targetAgent,
        form: {
          name: targetAgent.name || "",
          address: targetAgent.address || "",
          email: targetAgent.email || "",
          phone: targetAgent.phone || "",
          imageUrl: targetAgent.imageUrl || "",
          status: targetAgent.status || "active",
        },
        saving: false,
        error: "",
      });
    } else {
      setModalState({
        show: true,
        mode: "add",
        targetAgent: null,
        form: initialForm,
        saving: false,
        error: "",
      });
    }
  };

  const closeModal = () => {
    setModalState({
      show: false,
      mode: "add",
      targetAgent: null,
      form: initialForm,
      saving: false,
      error: "",
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
  };

  const submitModal = async (event) => {
    event.preventDefault();
    setModalState((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const trimmedName = modalState.form.name.trim();
      const trimmedEmail = modalState.form.email.trim();
      if (!trimmedName || !trimmedEmail) {
        throw new Error("Name and email are required.");
      }

      const payload = {
        name: trimmedName,
        address: modalState.form.address.trim(),
        email: trimmedEmail,
        phone: modalState.form.phone.trim(),
        imageUrl: modalState.form.imageUrl.trim(),
        status: modalState.form.status,
      };

      const config = {
        method: modalState.mode === "edit" ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const endpoint =
        modalState.mode === "edit" && modalState.targetAgent?.id
          ? ADMIN_AGENTS_UPDATE_ENDPOINT(modalState.targetAgent.id)
          : modalState.mode === "edit" && modalState.targetAgent?._id
          ? ADMIN_AGENTS_UPDATE_ENDPOINT(modalState.targetAgent._id)
          : ADMIN_AGENTS_ENDPOINT;

      const response = await fetch(endpoint, config);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Unable to save agent.");
      }

      closeModal();
      fetchAgents();
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        error: err.message || "Unable to save agent.",
      }));
    } finally {
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const deleteAgent = async (id) => {
    if (!window.confirm("Are you sure you want to delete this agent?")) {
      return;
    }
    setDeletingId(id);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_AGENTS_UPDATE_ENDPOINT(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Unable to delete agent.");
      }

      fetchAgents();
    } catch (err) {
      setError(err.message || "Unable to delete agent.");
    } finally {
      setDeletingId("");
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusSelection("");
    setStatusFilter("");
    setPage(1);
  };

  const applyFilters = () => {
    setSearchTerm(searchInput.trim());
    setStatusFilter(statusSelection.trim());
    setPage(1);
  };

  const renderStatusPill = (status) => {
    const normalized = typeof status === "string" ? status.toLowerCase() : "";
    if (normalized === "inactive") {
      return <span className="status-pill inactive">Inactive</span>;
    }
    return <span className="status-pill active">Active</span>;
  };

  const getInitials = (name = "") => {
    const trimmed = name.trim();
    if (!trimmed) {
      return "?";
    }
    const parts = trimmed.split(" ").filter(Boolean);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  return (
    <Styles>
      <AgentModalStyles />
      <div className="main-wrapper admin-agents-page">
        <HeaderTwo />
        <BreadcrumbBox title="Manage Agents" />

        <section className="agents-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <div className="actions">
                      <Button onClick={() => openModal("add")}>Add Agent</Button>
                      <div className="filters">
                        <input
                          type="text"
                          placeholder="Search agents..."
                          value={searchInput}
                          onChange={(event) => setSearchInput(event.target.value)}
                          disabled={loading}
                        />
                        <select
                          value={statusSelection}
                          onChange={(event) => setStatusSelection(event.target.value)}
                          disabled={loading}
                        >
                          <option value="">All statuses</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        <Button
                          variant="outline-secondary"
                          onClick={resetFilters}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="success"
                          onClick={applyFilters}
                          disabled={loading}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>

                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}

                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        <table>
                          <thead>
                            <tr>
                              <th>Agent</th>
                              <th>Phone</th>
                              <th>Address</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableRows.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center muted">
                                  No agents found.
                                </td>
                              </tr>
                            ) : (
                              tableRows.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    <div className="agent-info">
                                      <div className="agent-thumb">
                                        {row.imageUrl ? (
                                          <img src={row.imageUrl} alt={row.name} />
                                        ) : (
                                          getInitials(row.name)
                                        )}
                                      </div>
                                      <div className="details">
                                        <span className="name">{row.name}</span>
                                        <span className="email">{row.email}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>{row.phone || "—"}</td>
                                  <td>{row.address || "—"}</td>
                                  <td>{renderStatusPill(row.status)}</td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => openModal("edit", row.raw)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => deleteAgent(row.id)}
                                        disabled={deletingId === row.id}
                                      >
                                        {deletingId === row.id ? "Deleting..." : "Delete"}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>

                        {tableRows.length > 0 && (
                          <div className="pagination-controls">
                            <div className="page-info">
                              Page {pagination.page} of {pagination.totalPages} •{" "}
                              {pagination.totalItems} total
                            </div>
                            <div className="page-actions">
                              <Button
                                variant="outline-secondary"
                                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                disabled={page <= 1 || loading}
                              >
                                Previous
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() =>
                                  setPage((prev) =>
                                    Math.min(
                                      prev + 1,
                                      pagination.totalPages || prev + 1
                                    )
                                  )
                                }
                                disabled={
                                  page >= (pagination.totalPages || 1) || loading
                                }
                              >
                                Next
                              </Button>
                              <select
                                value={pageSize}
                                onChange={(event) => {
                                  setPageSize(Number(event.target.value));
                                  setPage(1);
                                }}
                                disabled={loading}
                              >
                                {[10, 20, 50, 100].map((size) => (
                                  <option key={size} value={size}>
                                    {size} / page
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        dialogClassName="admin-agents-modal"
        size="lg"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "add" ? "Add Agent" : "Edit Agent"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalState.error && (
              <Alert variant="danger">{modalState.error}</Alert>
            )}
            <Row>
              <Col md="6">
                <Form.Group controlId="agent_name">
                  <Form.Label>Agent Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={modalState.form.name}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="agent_email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={modalState.form.email}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="agent_phone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={modalState.form.phone}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="agent_status">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    as="select"
                    name="status"
                    value={modalState.form.status}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="12">
                <Form.Group controlId="agent_address">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    value={modalState.form.address}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="Office address (optional)"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="12">
                <Form.Group controlId="agent_image_url">
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="imageUrl"
                    value={modalState.form.imageUrl}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="https://example.com/photo.jpg"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={modalState.saving}
            >
              {modalState.saving ? "Saving..." : "Save Agent"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminAgents;
